import { useState } from 'react';
import { usePopper } from 'react-popper';

import { Placement, VirtualElement } from '@popperjs/core';

export type HintVirtualElement = Element | VirtualElement;

export interface HintHookOptions {
  placement?: Placement;
  offset?: [number, number];
}

/**
 * Hook to use the hint for handling as popper element
 */
export const useHint = <
  TElement extends HintVirtualElement = HintVirtualElement,
  TTarget extends HTMLElement = HTMLElement
>(
  opts?: HintHookOptions
): [React.RefCallback<TElement>, React.HTMLAttributes<TTarget>] => {
  const { placement = 'bottom', offset = [0, 8] } = { ...opts };
  const [element, setElement] = useState<TElement | null>(null);
  const [popperElement, setPopperElement] = useState<TTarget | null>(null);
  const { styles, attributes } = usePopper(element, popperElement, {
    placement,
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: offset,
        },
        enabled: !!offset,
      },
    ],
  });

  const props = { ref: setPopperElement, style: styles.popper, ...attributes.popper };

  return [setElement as React.RefCallback<TElement>, props] as const;
};
